import type { RouteRecordRaw } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useOrganizationConfig } from "@/stores/organizationConfig";
import { requireMimboxToExist } from "../guards";
import { qrCodesConverter } from "@mimsights-shared/converters/frontend";

export const qrCodeRoutes: RouteRecordRaw[] = [
  {
    path: "/portal/qr/:id",
    name: "qr-code-portal",
    meta: {
      hideSidebar: true,
    },
    // @ts-ignore TODO: Convert view to typescrit
    component: () => import("../../views/404.vue"), // Fallback, always redirected by beforeEnter
    beforeEnter: async (to, from) => {
      const { collection, doc, getDoc } = await import("firebase/firestore");
      const { db } = await import("../../mixins/firebase");
      const id = to.params.id as string;

      const documentRef = doc(collection(db, "qr_codes"), id).withConverter(qrCodesConverter);
      const qrCodeMetadataDocument = await getDoc(documentRef);
      if (!qrCodeMetadataDocument.exists()) {
        return { name: "404" };
      }
      const qrCodeMetadata = qrCodeMetadataDocument.data();

      if (qrCodeMetadata.configId) {
        const organizationConfig = useOrganizationConfig();
        await organizationConfig.load(qrCodeMetadata.configId, { outsideTree: true });
      }

      if (!qrCodeMetadata.serial_nr) {
        // Redirect to Connect QR
        const cameFromQrCodeConnect = from.name === "qr-code-connect";
        if (cameFromQrCodeConnect) {
          return { name: "404" };
        }
        return { name: "qr-code-connect", params: { id } };
      }
      if (!qrCodeMetadata.status) {
        return { name: "qr-code-install-start", params: { id } };
      }
      if (qrCodeMetadata.mimbox_id) {
        return {
          name: "public-qr",
          params: { id: qrCodeMetadata.mimbox_id, qrId: id },
        };
      }
      window.location.href = "https://www.mimbly.se/";
      return true;
    },
  },
  {
    path: "/qr/:id",
    beforeEnter: async (to, _from) => {
      document.body.classList.add("mobile");
      const { collection, doc, getDoc } = await import("firebase/firestore");
      const { db } = await import("../../mixins/firebase");
      const id = to.params.id as string;
      const documentRef = doc(collection(db, "qr_codes").withConverter(qrCodesConverter), id);
      const qrCodeMetadataDocument = await getDoc(documentRef);
      if (!qrCodeMetadataDocument.exists()) {
        return { name: "404" };
      }

      const qrCodeMetadata = qrCodeMetadataDocument.data();
      if (qrCodeMetadata.configId) {
        const organizationConfig = useOrganizationConfig();
        await organizationConfig.load(qrCodeMetadata.configId, { outsideTree: true });
      }

      return true;
    },
    meta: {
      noMargin: true,
      hideSidebar: true,
    },
    children: [
      {
        path: "connect",
        name: "qr-code-connect",
        component: () => import("../../views/Admin/QRCodeConnect.vue"),
        meta: {
          requiresAuth: true,
          requiresStaff: true,
          transitionName: "fade",
        },
        beforeEnter: [
          async (to, _from) => {
            const { collection, doc, getDoc } = await import("firebase/firestore");
            const { db } = await import("../../mixins/firebase");
            const id = to.params.id as string;

            const documentRef = doc(collection(db, "qr_codes"), id);
            const qrCodeMetadataDocument = await getDoc(documentRef);
            if (!qrCodeMetadataDocument.exists()) {
              return { name: "404" };
            }

            const qrCodeMetadata = qrCodeMetadataDocument.data() as {
              serial_nr?: string;
            };
            if (qrCodeMetadata.serial_nr) {
              // Nothing to do in this view.
              // allow portal to deside where to go
              return {
                name: "qr-code-portal",
                params: { id },
              };
            }

            return true;
          },
        ],
      },
      {
        path: "passcode",
        name: "qr-code-passcode",
        meta: {
          transitionName: "fade",
        },
        component: () => import("../../views/LoginPasscodeView.vue"),
        beforeEnter: async (to, _from) => {
          const authStore = useAuthStore();
          if (!authStore.is.passcode() && !authStore.is.staff()) {
            return true;
          }

          if (to.query.redirect && typeof to.query.redirect === "string") {
            return {
              path: to.query.redirect,
            };
          }
          return {
            name: "qr-code-portal",
            params: to.params,
          };
        },
      },
      {
        path: "install-start",
        name: "qr-code-install-start",
        component: () => import("../../views/QRCode/InstallMimboxWizard/Start.vue"),
        meta: { requiresPasscode: true },
        beforeEnter: [requireMimboxToExist],
      },
      {
        path: "install",
        component: () => import("../../views/QRCode/InstallMimboxWizard/Home.vue"),
        meta: {
          requiresPasscode: true,
          transitionName: "fade",
          stepPathNames: [
            "qr-code-install",
            "qr-code-install-additional-info",
            "qr-code-install-name",
            "qr-code-install-images",
            "qr-code-install-installer",
            "qr-code-install-comments",
          ],
        },
        beforeEnter: [requireMimboxToExist],
        children: [
          {
            path: "",
            name: "qr-code-install",
            component: () => import("../../views/QRCode/InstallMimboxWizard/Address.vue"),
          },
          {
            path: "additional-info",
            name: "qr-code-install-additional-info",
            component: () => import("../../views/QRCode/InstallMimboxWizard/AdditionalInfo.vue"),
          },
          {
            path: "name",
            name: "qr-code-install-name",
            component: () => import("../../views/QRCode/InstallMimboxWizard/MimboxName.vue"),
          },
          {
            path: "images",
            name: "qr-code-install-images",
            component: () => import("../../views/QRCode/InstallMimboxWizard/Images.vue"),
          },
          {
            path: "installer",
            name: "qr-code-install-installer",
            component: () => import("../../views/QRCode/InstallMimboxWizard/Installer.vue"),
          },
          {
            path: "comments",
            name: "qr-code-install-comments",
            component: () => import("../../views/QRCode/InstallMimboxWizard/Comments.vue"),
          },
          {
            path: "commit",
            name: "qr-code-install-commit",
            component: () => import("../../views/QRCode/InstallMimboxWizard/Commit.vue"),
            meta: {
              noHeader: true,
              noFooter: true,
            },
          },
        ],
      },
      {
        path: "troubleshoot",
        name: "qr-code-troubleshooting-guide",
        meta: { requiresPasscode: true },
        beforeEnter: [requireMimboxToExist],
        component: () => import("../../views/QRCode/ServicePortal/troubleshootingGuides/Home.vue"),
      },
      {
        path: "troubleshoot/:code",
        name: "qr-code-troubleshoot-error",
        meta: { requiresPasscode: true },
        beforeEnter: [requireMimboxToExist],
        component: () =>
          import("../../views/QRCode/ServicePortal/troubleshootingGuides/TroubleshootError.vue"),
      },
      {
        path: "service",
        meta: { requiresPasscode: true },
        beforeEnter: [requireMimboxToExist],
        component: () => import("../../views/QRCode/ServicePortal/Home.vue"),
        children: [
          {
            path: "",
            name: "qr-code-service",
            component: () => import("../../views/QRCode/ServicePortal/Details.vue"),
          },
          {
            path: "log",
            name: "qr-code-service-log",
            component: () => import("../../views/QRCode/ServicePortal/Log.vue"),
          },
          {
            path: "add-log",
            name: "qr-code-service-log-add",
            component: () => import("../../views/QRCode/ServicePortal/AddLog.vue"),
            children: [
              {
                path: "repair",
                component: () => import("../../views/QRCode/ServicePortal/AddLogForms/Repair.vue"),
                name: "qr-code-service-log-add-repair",
              },
              {
                path: "service",
                component: () => import("../../views/QRCode/ServicePortal/AddLogForms/Service.vue"),
                name: "qr-code-service-log-add-service",
              },
              {
                path: "repair-service",
                component: () =>
                  import("../../views/QRCode/ServicePortal/AddLogForms/RepairAndService.vue"),
                name: "qr-code-service-log-add-repair-service",
              },
              {
                path: "comment",
                component: () => import("../../views/QRCode/ServicePortal/AddLogForms/Comment.vue"),
                name: "qr-code-service-log-add-comment",
              },
            ],
          },
          {
            path: "guides",
            name: "qr-code-service-guides",
            component: () => import("../../views/QRCode/ServicePortal/Guides.vue"),
          },
        ],
      },
      {
        path: "waiting",
        name: "qr-code-waiting",
        meta: {
          transitionName: "fade",
        },
        component: () => import("../../views/QRCode/WaitingForMimbox.vue"),
        beforeEnter: async (to, _from) => {
          const { collection, doc, getDoc } = await import("firebase/firestore");
          const { db } = await import("../../mixins/firebase");
          const id = to.params.id as string;

          const documentRef = doc(collection(db, "qr_codes"), id);
          const qrCodeMetadataDocument = await getDoc(documentRef);
          if (!qrCodeMetadataDocument.exists()) {
            return { name: "404" };
          }
          const qrCodeMetadata = qrCodeMetadataDocument.data() as {
            mimbox_id?: string;
          };
          if (qrCodeMetadata.mimbox_id) {
            return {
              name: "qr-code-portal",
              params: { id },
            };
          }
          return true;
        },
      },
    ],
  },
];
